import { useMemo, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { LinkSimple } from '@hivebrite/design-system/Link';

import { useListContext } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/contexts/ListContext';
import type { TopicsWrapperProps } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/TopicsWrapper';
import type { ListLayoutsDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import { JoinGroupButton } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/JoinGroupButton';
import RequestGroupModal from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/RequestGroupModal';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ArticleText from 'hb-react/shared/apps/Articles/ArticlePublished/ArticleText';
import { GoToGroupButtonWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicActions/GoToGroupButton';
import { TopicDetailsModalWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDetailsModal';
import { getTopicImmersiveMenuHref } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicItem';
import { useTopicActionButton } from 'hb-react/shared/apps/TopicsIndexV2/hooks/useTopicActionButton';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';

const ListItem = ({
  dynamicAttributes,
  isBackOffice,
  networkRootUrl,
  topic: fetchedTopic,
}: Pick<TopicsWrapperProps, 'isBackOffice' | 'networkRootUrl'> & {
  dynamicAttributes?: ListLayoutsDynamicAttributes;
  topic: ITopic;
}) => {
  const [topic, setTopic] = useState(fetchedTopic);

  const {
    approval_id: approvalId,
    cover_picture_small_url: coverPicture,
    description,
    id,
    landing_page: landingPage,
    location,
    name,
    total_followings: totalFollowings,
    white_label_url: whiteLabelUrl,
  } = topic;

  const {
    displayAttendees,
    displayDescription,
    displayImage,
    displayLocation,
    displayTextLink,
    displayTitle,
  } = dynamicAttributes?.fields || {};

  const { display: displayButtons } = dynamicAttributes?.button || {};

  const {
    setHoverEffectImage,
    hoverEffectImage,
    hoverEffectTitle,
    handleMouseEnterInTitle,
    handleMouseLeaveInTitle,
  } = useListContext();

  const [displayRequestModal, setDisplayRequestModal] = useState(false);
  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);
  const [requestApproval, setRequestApproval] = useState({
    id: approvalId,
  });
  const topicHref = useMemo(() => {
    if (isBackOffice) {
      return '#';
    }
    if (whiteLabelUrl) {
      return getTopicImmersiveMenuHref(whiteLabelUrl, landingPage, id);
    }

    return `${networkRootUrl}${landingPage}`;
  }, [id, isBackOffice, landingPage, networkRootUrl, whiteLabelUrl]);

  const handleMouseEnterInImage = () =>
    setHoverEffectImage({ cardId: id, isHover: true });

  const handleMouseLeaveInImage = () =>
    setHoverEffectImage({ cardId: null, isHover: false });

  const handleDisplayLocation = (location: {
    city?: string;
    country?: string;
  }): string | null => {
    if (!displayLocation) return null;
    if (!location?.city && !location?.country) return '';
    if (location?.city && !location?.country) return `${location.city}.`;
    if (!location?.city && location?.country) return `${location.country}.`;

    return `${location.city}, ${location.country}.`;
  };

  const handleOpenModal = () => {
    !isBackOffice && setDisplayDetailsModal(true);
  };

  const target = whiteLabelUrl ? '_blank' : undefined;
  const { currentUser } = useDefaultAppContexts();
  const isConnected = !!currentUser || isBackOffice;
  const { buttonLabel, buttonA11yLabel } = useTopicActionButton(topic);

  const showDescription = displayDescription && description;

  return (
    <Wrapper $displayDescription={!!displayDescription}>
      {displayImage && (
        <ImgWrapper>
          <LinkSimpleWrapper to={topicHref}>
            <StyledImg
              $hoverOnTitle={hoverEffectTitle.isHover}
              $isRightCard={hoverEffectTitle.cardId === id}
              onMouseEnter={handleMouseEnterInImage}
              onMouseLeave={handleMouseLeaveInImage}
              src={coverPicture}
            />
          </LinkSimpleWrapper>
        </ImgWrapper>
      )}
      <Box flex={1} flexDirection="column">
        <Box flexDirection="column">
          {displayTitle && (
            <LinkSimpleWrapper to={topicHref}>
              <HeaderTitleStyled
                $hoverOnImage={hoverEffectImage?.isHover}
                $isRightCard={hoverEffectImage?.cardId === id}
                onMouseEnter={() => handleMouseEnterInTitle(id)}
                onMouseLeave={() => handleMouseLeaveInTitle()}
              >
                <ClampedLinkWorkaround
                  clamp={2}
                  lineHeight="3rem"
                  margin={-2}
                  padding={2}
                  useNativeClamp
                >
                  {name}
                </ClampedLinkWorkaround>
              </HeaderTitleStyled>
            </LinkSimpleWrapper>
          )}
          <Box>
            {displayAttendees &&
              totalFollowings !== null &&
              n__(
                `%{totalFollowings} member${
                  handleDisplayLocation(location) ? ' · ' : ''
                }`,
                `%{totalFollowings} members${
                  handleDisplayLocation(location) ? ' · ' : ''
                }`,
                totalFollowings,
              ).i__({
                // @ts-expect-error totalFollowings is a number
                totalFollowings,
              })}
            {handleDisplayLocation(location)}
          </Box>
          {displayButtons && (
            <Buttons>
              {!topic.closed && (
                <Box width="100%" mr={3}>
                  <JoinGroupButton
                    isBackOffice={isBackOffice}
                    isConnected={isConnected}
                    labelButton={buttonLabel}
                    labelA11yButton={buttonA11yLabel}
                    setDisplayRequestModal={setDisplayRequestModal}
                    setTopics={(topics) => setTopic(topics[0])}
                    topic={topic}
                    topics={[topic]}
                  />
                </Box>
              )}
              <Box width="100%">
                <GoToGroupButtonWithoutContext
                  href={topicHref}
                  target={target}
                  topicName={topic?.name}
                />
              </Box>
            </Buttons>
          )}
        </Box>
        {showDescription && (
          <DescriptionWrapper>
            <Dotdotdot clamp={2} useNativeClamp>
              <ArticleText text={description} />
            </Dotdotdot>
          </DescriptionWrapper>
        )}
        {displayTextLink && (
          <Box
            as="a"
            onClick={handleOpenModal}
            mt={{
              _: showDescription ? '2px' : 2,
              l: showDescription ? 1 : 2,
            }}
          >
            {s__('Builder|See more')}
          </Box>
        )}
      </Box>
      {displayRequestModal && (
        <RequestGroupModal
          href={topicHref}
          requestApproval={requestApproval}
          setDisplayRequestModal={setDisplayRequestModal}
          setRequestApproval={setRequestApproval}
          setTopics={(topics) => setTopic(topics[0])}
          target={target}
          topic={topic}
          topics={[topic]}
        />
      )}
      {displayDetailsModal && (
        <TopicDetailsModalWithoutContext
          isConnectedUser={isConnected}
          setDisplayDetailsModal={setDisplayDetailsModal}
          topic={topic}
        >
          <Box ml={4} flexDirection="column">
            <JoinGroupButton
              isBackOffice={isBackOffice}
              isConnected={isConnected}
              labelButton={buttonLabel}
              labelA11yButton={buttonA11yLabel}
              setDisplayRequestModal={setDisplayRequestModal}
              setTopics={(topics) => setTopic(topics[0])}
              topic={topic}
              topics={[topic]}
            />
            <Box mt={4}>
              <GoToGroupButtonWithoutContext
                href={topicHref}
                target={target}
                topicName={topic?.name}
              />
            </Box>
          </Box>
        </TopicDetailsModalWithoutContext>
      )}
    </Wrapper>
  );
};

export default ListItem;

const Wrapper = styled(Box).attrs(
  ({ $displayDescription }: { $displayDescription: boolean }) => ({
    display: 'flex',
    flexDirection: {
      _: 'column',
      m: 'row',
    },
    height: {
      m: $displayDescription ? '220px' : '170px',
      l: $displayDescription ? '225px' : '175px',
    },
    mb: {
      _: 6,
      l: 8,
    },
  }),
)<{ $displayDescription: boolean }>`
  width: 100%;
`;

const StyledImg = styled.img`
  cursor: pointer;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  width: 100%;
  ${({
    $hoverOnTitle,
    $isRightCard,
  }: {
    $hoverOnTitle: boolean;
    $isRightCard: boolean;
  }) =>
    $hoverOnTitle &&
    $isRightCard &&
    css`
      transform: scale(1.1) !important;
    `}
  &:hover {
    transform: scale(1.1) !important;
  }
`;

const HeaderTitleStyled = styled(Box).attrs({
  font: 'h1-semibold',
  mb: {
    _: 1,
    l: '6px',
  },
  display: 'block',
})<{
  $hoverOnImage: boolean;
  $isRightCard: boolean;
}>`
  ${({ $hoverOnImage, $isRightCard }) =>
    $hoverOnImage &&
    $isRightCard &&
    css`
       {
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.link};
      }
    `}
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const DescriptionWrapper = styled(Box).attrs(() => ({
  fontSize: '14px',
  mt: 2,
}))`
  iframe,
  img,
  video {
    display: none;
  }
`;

const ImgWrapper = styled(Box).attrs(() => ({
  height: { _: '189px', m: '125px', l: '168px', xl: '181px' },
  mb: { _: 3, m: 0 },
  mr: { m: '20px', l: 6 },
  overflow: 'hidden',
  width: { _: '343px', m: '226px', l: '304px', xl: '327px' },
}))`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const Buttons = styled(Box).attrs({
  mt: { _: 4, m: 3 },
  maxWidth: '206px',
})``;

const LinkSimpleWrapper = styled(LinkSimple)`
  text-decoration: none !important;

  &:hover {
    text-decoration: none !important;
  }
`;
